/**
 * LocationDropdown Component
 * Reusable dropdown for selecting dealership locations
 * DepartmentManager users only see their assigned locations
 * Admin/Executive/GM can see "Consolidated" view (aggregated across all locations)
 */

import React, { useMemo, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { FEATURE_FLAGS } from '../config/featureFlags';

interface LocationDropdownProps {
  value: number;
  onChange: (locationId: number) => void;
  disabled?: boolean;
  className?: string;
}

// Consolidated view (id: 0) is at the top for users with access
export const LOCATIONS = [
  { id: 0, name: 'Consolidated' },
  { id: 1, name: "Darling's Ford VW Audi" },
  { id: 2, name: "Darling's Honda Nissan Volvo" },
  { id: 3, name: "Darling's Auto Mall" },
  { id: 4, name: "Darling's Chrysler Dodge Hyundai" },
  { id: 5, name: "Darlings Chevrolet" },
  { id: 10, name: "Brunswick Ford" },
  { id: 11, name: "Darlings CDJR Newport" },
  { id: 12, name: "Darling's Green Point" },
  { id: 14, name: "Darling's Kia" },
  { id: 44, name: "Darling's Undercoating & More" },
];

// Individual locations only (excludes Consolidated)
export const INDIVIDUAL_LOCATIONS = LOCATIONS.filter(loc => loc.id !== 0);

export function getAvailableLocations(
  userRole: string | null | undefined,
  assignedLocationIds: number[],
  consolidatedEnabled: boolean
) {
  const isLocationLimited = userRole === 'DepartmentManager' || userRole === 'TeamServiceManager';
  const hasAssignments = assignedLocationIds.length > 0;

  if (userRole === 'TeamServiceManager' && !hasAssignments) {
    return [];
  }
  if (isLocationLimited && hasAssignments) {
    return INDIVIDUAL_LOCATIONS.filter(loc => assignedLocationIds.includes(loc.id));
  }
  if (userRole === 'MarketingManager') {
    return INDIVIDUAL_LOCATIONS;
  }
  if (!consolidatedEnabled) {
    return INDIVIDUAL_LOCATIONS;
  }
  return LOCATIONS;
}

export const LocationDropdown: React.FC<LocationDropdownProps> = ({
  value,
  onChange,
  disabled = false,
  className = ''
}) => {
  const { userRole, assignedLocationIds } = useAuth();

  // Filter locations based on role and feature flags
  // - Consolidated (id: 0) only shown if feature flag enabled AND user has access to all locations
  // - DeptMgr with assignments only sees their assigned locations (never Consolidated)
  // - Admin/Executive/GM see all locations including Consolidated
  const availableLocations = useMemo(
    () => getAvailableLocations(userRole, assignedLocationIds, FEATURE_FLAGS.CONSOLIDATED_VIEW_ENABLED),
    [userRole, assignedLocationIds]
  );

  // Fallback: auto-select the first available location if current value isn't in the list.
  // Primary validation happens in useLocationParam; this handles edge cases.
  useEffect(() => {
    if (availableLocations.length > 0) {
      const currentValueAvailable = availableLocations.some(loc => loc.id === value);
      if (!currentValueAvailable) {
        onChange(availableLocations[0].id);
      }
    }
  }, [availableLocations, value, onChange]);

  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={disabled || availableLocations.length === 0}
      className={`border border-gray-300 rounded px-2 py-1.5 text-sm bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${className}`}
    >
      {availableLocations.length === 0 ? (
        <option value="">No location assigned</option>
      ) : (
        availableLocations.map((location) => (
          <option key={location.id} value={location.id}>
            {location.name}
          </option>
        ))
      )}
    </select>
  );
};
